import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import FormError from 'components/common/FormError';
import CheckboxField from 'components/fields/CheckboxField';
import IconButton from 'components/common/IconButton';
import IconCopy from 'components/icons/IconCopy';
import IconArrowBottom from 'components/icons/IconArrowBottom';

import {
  createHCPCSCode,
  updateHCPCSCode,
} from 'redux/modules/hcpcsCodes';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

const styles = theme => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  fields: {
    display: 'flex',
  },
  textField: {
    maxWidth: '25%',
    flexBasis: '25%',
    '& input': {
      paddingLeft: 5,
      paddingRight: 5,
      textAlign: 'center',
    },
  },
  textFieldNotFirst: {
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: -2,
      width: 3,
      backgroundColor: theme.palette.grey[100],
      borderTop: `1px solid ${theme.palette.grey[600]}`,
      borderBottom: `1px solid ${theme.palette.grey[600]}`,
      zIndex: 1,
    },
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 7,
      bottom: 7,
      left: -1,
      width: 1,
      backgroundColor: theme.palette.grey[600],
      zIndex: 2,
    },
  },
  inputRootNotFirst: {
    borderLeft: 'none',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  inputRootNotLast: {
    borderRight: 'none',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  checkboxFieldSameAs: {
    marginLeft: 8,
    marginBottom: -8,
    marginTop: -8,
    display: 'inline-flex',
  },
  fieldGroup: {
    marginTop: 16,
    position: 'relative',
    padding: '24px 16px 16px 16px',
    border: `1px dashed ${theme.palette.grey[600]}`,
    borderRadius: 4,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 16,
      marginRight: 24,
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      width: 24,
      height: 25,
      backgroundColor: theme.palette.common.white,
      top: -4,
      right: -12,
      zIndex: 1,
    },
  },
  fieldGroupLabel: {
    top: -4,
    left: -4,
    position: 'absolute',
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    padding: '4px 4px 4px 0',
    textAlign: 'center',
    fontWeight: 700,
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.common.white,
    transform: 'none',
  },
  fieldGroupLabelMedicare: {
    color: theme.palette.notification[700],
  },
  buttonGroupCopy: {
    position: 'absolute',
    right: -24,
    top: -16,
    zIndex: 10,
    '&:hover $iconArrowBottom': {
      color: theme.palette.notification[700],
      top: 8,
    },
    '&:hover $iconArrowTop': {
      color: theme.palette.primary[700],
      top: 0,
    },
  },
  iconCopy: {
    width: 20,
    height: 20,
  },
  iconArrowBottom: {
    position: 'absolute',
    top: 4,
    right: 2,
    width: 12,
    height: 12,
    transition: theme.transitions.create(['top'], {
      duration: theme.transitions.duration.short,
    }),
  },
  iconArrowTop: {
    position: 'absolute',
    top: 4,
    right: 2,
    width: 12,
    height: 12,
    transform: 'rotate(180deg)',
    transition: theme.transitions.create(['top'], {
      duration: theme.transitions.duration.short,
    }),
  },
  buttonDescriptionCopy: {
    margin: -8,
    position: 'relative',
    verticalAlign: 'middle',
    zIndex: 10,
    '&:hover $iconArrowBottom': {
      color: theme.palette.notification[700],
      top: 8,
    },
    '&:hover $iconArrowTop': {
      color: theme.palette.primary[700],
      top: 0,
    },
  },
  textFieldAdornmentText: {
    width: 44,
    textAlign: 'right',
  },
  typographyState: {
    marginBottom: 8,
  },
  typographyLabel: {
    fontWeight: 700,
    color: theme.palette.grey[600],
  },
  gridFooter: {
    marginTop: 0,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {
      ...formValues['Default Modifiers'] ? {
        'Default Modifiers': formValues['Default Modifiers'].join(''),
      } : {},
    });

    normalizedValues['HCPC S Code Price'] = [
      {
        'Price Type': 'Medicaid',
        ...values['Medicaid Price ID'] ? {
          ID: values['Medicaid Price ID'],
        } : {
          State: props.State,
        },
        ...normalizedValues['Medicaid Description'] ? {
          'Description': normalizedValues['Medicaid Description'],
        } : {},
        ...normalizedValues['Medicaid Sale Price'] ? {
          'Sale Price': normalizedValues['Medicaid Sale Price'].replace(/[$,]/g, ''),
        } : {},
        ...normalizedValues['Medicaid Sale Price Rural'] ? {
          'Sale Price Rural': normalizedValues['Medicaid Sale Price Rural'].replace(/[$,]/g, ''),
        } : {},
        ...normalizedValues['Medicaid Rental Price'] ? {
          'Rental Price': normalizedValues['Medicaid Rental Price'].replace(/[$,]/g, ''),
        } : {},
        ...normalizedValues['Medicaid Rental Price Rural'] ? {
          'Rental Price Rural': normalizedValues['Medicaid Rental Price Rural'].replace(/[$,]/g, ''),
        } : {},
      },
      {
        'Price Type': 'Medicare',
        ...values['Medicare Price ID'] ? {
          ID: values['Medicare Price ID'],
        } : {
          State: props.State,
        },
        ...normalizedValues['Medicare Description'] ? {
          'Description': normalizedValues['Medicare Description'],
        } : {},
        ...normalizedValues['Medicare Sale Price'] ? {
          'Sale Price': normalizedValues['Medicare Sale Price'].replace(/[$,]/g, ''),
        } : {},
        ...normalizedValues['Medicare Sale Price Rural'] ? {
          'Sale Price Rural': normalizedValues['Medicare Sale Price Rural'].replace(/[$,]/g, ''),
        } : {},
        ...normalizedValues['Medicare Rental Price'] ? {
          'Rental Price': normalizedValues['Medicare Rental Price'].replace(/[$,]/g, ''),
        } : {},
        ...normalizedValues['Medicare Rental Price Rural'] ? {
          'Rental Price Rural': normalizedValues['Medicare Rental Price Rural'].replace(/[$,]/g, ''),
        } : {},
      },
    ];

    normalizedValues['HCPC S Code Price'] = normalizedValues['HCPC S Code Price']
      .filter(priceItem => Object.keys(priceItem).length > 2);
    if (normalizedValues['HCPC S Code Price'].length === 0) {
      delete normalizedValues['HCPC S Code Price'];
    }
    delete normalizedValues['Medicaid Description'];
    delete normalizedValues['Medicaid Sale Price'];
    delete normalizedValues['Medicaid Sale Price Rural'];
    delete normalizedValues['Medicaid Rental Price'];
    delete normalizedValues['Medicaid Rental Price Rural'];
    delete normalizedValues['Medicare Description'];
    delete normalizedValues['Medicare Sale Price'];
    delete normalizedValues['Medicare Sale Price Rural'];
    delete normalizedValues['Medicare Rental Price'];
    delete normalizedValues['Medicare Rental Price Rural'];
    
    return normalizedValues;
  };
  if (props.ID) {
    if (props.pristine) {
      throw new SubmissionError({
        _error: 'Nothing to submit',
      });
    }
    const diffValues = objectDiff(values, props.initialValues);
    return new Promise((resolve, reject) => {
      dispatch(updateHCPCSCode({
        values: {
          ID: props.ID,
          ...normalizeValues(diffValues),
          ...diffValues['Default Modifiers'] ? {
            'Default Modifiers': values['Default Modifiers'].join(''),
          } : {},
          ...diffValues['Default Modifiers'] && values['Default Modifiers'].filter(modifiersItem => modifiersItem).length === 0
            ? { 'Default Modifiers': null } : {},
        },
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.description });
        }
        throw new SubmissionError(err);
      });
  }

  return new Promise((resolve, reject) => {
    dispatch(createHCPCSCode({
      values: normalizeValues(values),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.description });
      }
      throw new SubmissionError(err);
    });
}

const HCPCSCodeCreateEditForm = ({
  handleSubmit,
  className,
  error,
  onCopy,
  State,
  width,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      spacing={24}
      alignItems="flex-end"
    >
      <Grid item xs={3}>
        <Typography className={classes.typographyState}>
          <span className={classes.typographyLabel}>State:</span> {State}
        </Typography>
      </Grid>
      <Grid
        item
        xs={3}
      >
        <Field
          id="HCPC S Code"
          name="HCPC S Code"
          label="HCPCS Code"
          component={TextField}
          normalize={normalize.hcpcCode}
          validate={[validate.required, validate.hcpcCode]}
          maxLength="5"
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
      >
        <FormControl
          className={classes.root}
          component="div"
        >
          <FormLabel component="legend">
            Default Modifiers
          </FormLabel>
          <div className={classes.fields}>
            {['', '', '', ''].map((field, index) => (
              <Field
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                id={`Default Modifiers[${index}]`}
                name={`Default Modifiers[${index}]`}
                component={TextField}
                className={classNames(classes.textField, {
                  [classes.textFieldNotFirst]: index !== 0,
                })}
                inputRootClassName={classNames({
                  [classes.inputRootNotFirst]: index !== 0,
                  [classes.inputRootNotLast]: index !== 3,
                })}
                validate={[validate.minLength2]}
                normalize={normalize.modifiers}
                maxLength="2"
                fullWidth
              />
            ))}
          </div>
        </FormControl>
      </Grid>
    </Grid>
    <div className={classes.fieldGroup}>
      <Typography className={classes.fieldGroupLabel}>
        Medicaid
      </Typography>
      <IconButton
        className={classes.buttonGroupCopy}
        onClick={() => onCopy('Medicaid All')}
        tooltipContent="Copy all to Medicare"
      >
        <IconCopy className={classes.iconCopy} />
        <IconArrowBottom className={classes.iconArrowBottom} />
      </IconButton>
      <Grid
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <Field
            id="Medicaid Description"
            name="Medicaid Description"
            label="Description"
            labelEndAdornment={(
              <IconButton
                className={classes.buttonDescriptionCopy}
                onClick={(event) => onCopy('Medicaid Description')}
                tooltipContent="Copy Description to Medicare"
              >
                <IconCopy className={classes.iconCopy} />
                <IconArrowBottom className={classes.iconArrowBottom} />
              </IconButton>
            )}
            component={TextField}
            errorPlacement="bottom"
            multiline
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicaid Sale Price"
            name="Medicaid Sale Price"
            label={width === 'xs' ? 'Purchase Reimbs' : 'Purchase Reimbursement'}
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Urban:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicaid Rental Price"
            name="Medicaid Rental Price"
            label="Rental Reimbursement"
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Urban:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicaid Sale Price Rural"
            name="Medicaid Sale Price Rural"
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Rural:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicaid Rental Price Rural"
            name="Medicaid Rental Price Rural"
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Rural:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
      </Grid>
    </div>
    <div className={classes.fieldGroup}>
      <Typography className={classNames(classes.fieldGroupLabel, classes.fieldGroupLabelMedicare)}>
        Medicare
      </Typography>
      <IconButton
        className={classes.buttonGroupCopy}
        onClick={() => onCopy('Medicare All')}
        tooltipContent="Copy all to Medicaid"
      >
        <IconCopy className={classes.iconCopy} />
        <IconArrowBottom className={classes.iconArrowTop} />
      </IconButton>
      <Grid
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <Field
            id="Medicare Description"
            name="Medicare Description"
            label="Description"
            labelEndAdornment={(
              <IconButton
                className={classes.buttonDescriptionCopy}
                onClick={() => onCopy('Medicare Description')}
                tooltipContent="Copy Description to Medicaid"
              >
                <IconCopy className={classes.iconCopy} />
                <IconArrowBottom className={classes.iconArrowTop} />
              </IconButton>
            )}
            component={TextField}
            errorPlacement="bottom"
            multiline
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicare Sale Price"
            name="Medicare Sale Price"
            label={width === 'xs' ? 'Purchase Reimbs' : 'Purchase Reimbursement'}
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Urban:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicare Rental Price"
            name="Medicare Rental Price"
            label="Rental Reimbursement"
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Urban:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
      </Grid>
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicare Sale Price Rural"
            name="Medicare Sale Price Rural"
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Rural:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
        <Grid
          item
          xs={6}
        >
          <Field
            id="Medicare Rental Price Rural"
            name="Medicare Rental Price Rural"
            component={TextField}
            normalize={normalize.price}
            errorPlacement="bottom"
            fullWidth
            startAdornment={
              <InputAdornment position="start">
                <TextFieldAdornmentText className={classes.textFieldAdornmentText}>
                  Rural:
                </TextFieldAdornmentText>
              </InputAdornment>
            }
          />
        </Grid>
      </Grid>
    </div>
    <Grid
      className={classes.gridFooter}
      container
      justify="flex-start"
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="Do Not Override"
          name="Do Not Override"
          label="Do not override by file upload"
          component={CheckboxField}
        />
      </Grid>
    </Grid>
  </form>
);

HCPCSCodeCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  onCopy: PropTypes.func,
  State: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    textField: PropTypes.string.isRequired,
    checkboxFieldSameAs: PropTypes.string.isRequired,
  }).isRequired,
};

HCPCSCodeCreateEditForm.defaultProps = {
  className: '',
  error: null,
  onCopy: () => {},
};

export default reduxForm({
  form: 'hcpcsCodeCreate',
  onSubmit,
})(withStyles(styles)(withWidth()(HCPCSCodeCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/settings/HCPCSCodeCreateEditDialog/HCPCSCodeCreateEditForm.js